import { AxiosRequestConfig } from 'axios';

import { downloadFile } from './helpers';
import http from './http';

/**
 * Download HTTP utility function - similar to http() but for download operations
 *
 * @param {string} baseURL
 * @param {number} timeout
 * @returns
 */
const downloadHttp = (baseURL?: string, timeout?: number) => {
  const httpClient = http('json', baseURL, timeout);

  // CSV download with long polling: handles CSV generation, polling, and download
  function lpostcsv(path: string, payload: any, filename: string, config?: AxiosRequestConfig) {
    return httpClient.lpost(path, payload, config).then(response => {
      const csvData = response.payload.original;

      // Check if CSV data is empty or contains "No data available"
      if (
        !csvData ||
        csvData.includes('No data available') ||
        csvData.trim() === '' ||
        csvData.split('\n').length <= 1
      ) {
        return {
          success: true,
          message: 'Report processing complete, but no data available for the selected criteria.',
          isEmpty: true,
          data: null,
        };
      }

      // Convert CSV string to Blob and download
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      downloadFile(blob, filename);

      return {
        success: true,
        message: response.message || 'CSV downloaded successfully',
        isEmpty: false,
        data: csvData,
      };
    });
  }

  // PDF download with long polling: handles PDF generation, polling, and download
  function lpostpdf(path: string, payload: any, filename: string, config?: AxiosRequestConfig) {
    return httpClient.lpost(path, payload, config).then(async response => {
      const downloadUrl = response.payload.download_url;

      // Check if download_url exists
      if (!downloadUrl) {
        return {
          success: false,
          message: 'PDF generation failed - no download URL provided',
          isEmpty: true,
          data: null,
        };
      }

      try {
        // Fetch the PDF from the download URL
        const pdfResponse = await httpClient.get(downloadUrl, {
          responseType: 'blob',
          ...config,
        });

        // Create blob and download
        const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });

        downloadFile(blob, filename);

        return {
          success: true,
          message: response.message || 'PDF downloaded successfully',
          isEmpty: false,
          data: pdfResponse.data,
          downloadUrl: downloadUrl,
        };
      } catch (downloadError) {
        return {
          success: false,
          message: 'Failed to download PDF from server',
          isEmpty: true,
          data: null,
          downloadUrl: downloadUrl,
        };
      }
    });
  }

  return {
    lpostcsv,
    lpostpdf,
  };
};

export default downloadHttp;
